/**
 * Adaptive photo upload with stall detection and fallback to client-side compression.
 *
 * Strategy:
 * 1. Try original quality. Watch byte rate — abort if stalled.
 * 2. On stall/error, compress to 2048px JPEG and retry (a photo; a video is
 *    sent as it is — compressing can't shrink it).
 * 3. Further failures use exponential backoff with compressed version.
 *
 * Every try gives up when no bytes have moved for a while, so a dead
 * connection never holds the upload (and the queue behind it) until the
 * device itself gives up. Once every byte is sent, the server's answer gets
 * its own, longer limit: it's processing the photo then, not stalled. An
 * answer that trying again can't change (4xx other than 408/429) ends it at
 * once.
 */

import { compressImage } from "./imageCompress";

const STALL_THRESHOLD_BPS = 5 * 1024; // 5 KB/sec
const STALL_CHECK_AFTER_MS = 10 * 1000; // give 10s before checking for stall
const MAX_ORIGINAL_TIME_MS = 45 * 1000; // abort an original photo after 45s
const QUIET_ORIGINAL_MS = 8 * 1000; // an original photo: no progress this long is a stall
const QUIET_MS = 30 * 1000; // any other try: no progress this long is a stall
const ANSWER_TIMEOUT_MS = 120 * 1000; // every byte sent: the server answers within this
const RETRY_DELAYS_MS = [2000, 8000, 30000];

// An answer that trying again can't change.
const isPermanent = (status) => status >= 400 && status < 500 && status !== 408 && status !== 429;

/**
 * Upload a file with adaptive retry logic.
 *
 * @param {Object} opts
 * @param {File|Blob} opts.file - The file to upload
 * @param {string} opts.url - Full URL to POST to
 * @param {string} opts.fieldName - FormData field name (default "file")
 * @param {Object} opts.headers - Headers object (e.g. { Authorization: "Bearer ..." })
 * @param {Function} opts.onProgress - (state) => void — state is { phase, percent, compressed, attempt }
 * @param {AbortSignal} opts.signal - Optional abort signal
 * @returns {Promise<any>} parsed JSON response
 */
export async function adaptiveUpload({ file, url, fieldName = "file", headers = {}, onProgress, signal }) {
  let attempt = 0;
  let lastError = null;
  let useCompressed = false;
  let compressedFile = null;
  const compressible = !!file?.type && file.type.startsWith("image/");

  const notify = (state) => { if (onProgress) onProgress(state); };

  while (attempt < 1 + RETRY_DELAYS_MS.length) {
    if (signal?.aborted) throw new Error("Upload aborted");

    // Prepare file for this attempt
    let uploadFile = file;
    if (useCompressed) {
      if (!compressedFile) {
        notify({ phase: "compressing", percent: 0, compressed: true, attempt });
        try {
          compressedFile = await compressImage(file, { maxWidth: 2048, quality: 0.8 });
        } catch (err) {
          // Compression failed — fall back to original
          compressedFile = file;
        }
      }
      uploadFile = compressedFile;
    }

    try {
      notify({ phase: "uploading", percent: 0, compressed: useCompressed, attempt });
      const result = await xhrUpload({
        file: uploadFile,
        url,
        fieldName,
        headers,
        onProgress: (pct) => notify({ phase: "uploading", percent: pct, compressed: useCompressed, attempt }),
        signal,
        // The original photo's quick checks (time cap, rate) — they exist to
        // switch to the compressed copy.
        original: !useCompressed && compressible,
      });
      notify({ phase: "success", percent: 100, compressed: useCompressed, attempt });
      return result;
    } catch (err) {
      lastError = err;
      if (err.message === "Upload aborted" || signal?.aborted) throw err;
      // Too big for the server: a photo goes again compressed; anything else
      // the server won't take ends here.
      const tooBig = err.status === 413 && compressible && !useCompressed;
      if (isPermanent(err.status) && !tooBig) throw err;

      // First failure on an original photo → switch to compressed
      if (!useCompressed && compressible) {
        useCompressed = true;
        attempt = 1;
        notify({ phase: "retrying", percent: 0, compressed: true, attempt });
        continue;
      }

      // Compressed already (or a video) — schedule backoff retry
      if (attempt >= RETRY_DELAYS_MS.length) break;
      const delay = RETRY_DELAYS_MS[attempt];
      notify({ phase: "waiting", percent: 0, compressed: useCompressed, attempt, waitMs: delay });
      await sleep(delay, signal);
      attempt++;
    }
  }

  notify({ phase: "failed", percent: 0, compressed: useCompressed, attempt, error: lastError?.message });
  throw lastError || new Error("Upload failed after retries");
}

/**
 * Low-level XHR upload with progress events and stall detection.
 */
function xhrUpload({ file, url, fieldName, headers, onProgress, signal, original }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    formData.append(fieldName, file);

    let lastBytes = 0;
    let lastTime = Date.now();
    const startTime = lastTime;
    let sentAt = 0; // every byte sent: waiting for the server's answer
    let bpsCheckTimer = null;
    let settled = false;

    const cleanup = () => {
      if (bpsCheckTimer) clearInterval(bpsCheckTimer);
      if (signal) signal.removeEventListener("abort", handleAbort);
    };
    // Ends the try once: gives up on the request with this message.
    const giveUp = (message) => {
      if (settled) return;
      settled = true;
      cleanup();
      xhr.abort();
      reject(new Error(message));
    };

    const handleAbort = () => giveUp("Upload aborted");

    if (signal) signal.addEventListener("abort", handleAbort);

    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        onProgress(percent);
        lastBytes = e.loaded;
        lastTime = Date.now();
      }
    });
    xhr.upload.addEventListener("load", () => { sentAt = Date.now(); });

    xhr.addEventListener("load", () => {
      if (settled) return;
      settled = true;
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); }
        catch (e) { resolve(xhr.responseText); }
      } else {
        // Include the server's error message from the response body so the
        // user sees something more useful than "HTTP 500:" (statusText is
        // typically empty under HTTP/2).
        let serverMsg = "";
        try {
          const parsed = JSON.parse(xhr.responseText);
          serverMsg = parsed?.error || parsed?.message || xhr.responseText || "";
        } catch (_) {
          serverMsg = xhr.responseText || "";
        }
        const suffix = (xhr.statusText || serverMsg)
          ? `: ${xhr.statusText || serverMsg}`
          : "";
        const err = new Error(`HTTP ${xhr.status}${suffix}`);
        err.status = xhr.status;
        reject(err);
      }
    });
    xhr.addEventListener("error", () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error("Network error"));
    });
    xhr.addEventListener("abort", () => { cleanup(); });

    xhr.open("POST", url);
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.send(formData);

    bpsCheckTimer = setInterval(() => {
      const now = Date.now();
      // Every byte is out: the server is working on it — only its answer's
      // own limit applies (no progress events come now).
      if (sentAt) {
        if (now - sentAt > ANSWER_TIMEOUT_MS) giveUp("No answer from the server");
        return;
      }
      const elapsed = now - startTime;
      if (!original) {
        if (now - lastTime > QUIET_MS) giveUp("Upload stalled");
        return;
      }
      // An original photo: give up quickly so the compressed copy goes instead.
      if (elapsed > MAX_ORIGINAL_TIME_MS) return giveUp("Upload timeout");
      if (elapsed < STALL_CHECK_AFTER_MS) return;
      if (now - lastTime > QUIET_ORIGINAL_MS) return giveUp("Upload stalled");
      const bps = lastBytes / (elapsed / 1000);
      if (bps > 0 && bps < STALL_THRESHOLD_BPS && elapsed > STALL_CHECK_AFTER_MS + 5000) giveUp("Upload too slow");
    }, 2000);
  });
}

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener("abort", () => {
        clearTimeout(timer);
        reject(new Error("Upload aborted"));
      });
    }
  });
}
